import { tx } from "@/i18n/tx";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bell, BellOff, Loader2, Send } from "lucide-react";

// urlBase64 → Uint8Array (required by pushManager.subscribe)
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

function isSupported() {
  if (typeof window === "undefined") return false;
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

function isIOSNotStandalone() {
  const ua = navigator.userAgent || "";
  const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  if (!iOS) return false;
  const standalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;
  return !standalone;
}

export default function PushToggle() {
  const [supported, setSupported] = useState(true);
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [iosNeedsInstall, setIosNeedsInstall] = useState(false);

  useEffect(() => {
    if (!isSupported()) {
      setSupported(false);
      return;
    }
    if (isIOSNotStandalone()) {
      setIosNeedsInstall(true);
    }
    let cancelled = false;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!cancelled) setSubscribed(!!sub);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const enable = async () => {
    if (!isSupported()) return;
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error(tx("ui_pushtoggle.k42cae1"));
        return;
      }

      const reg = await navigator.serviceWorker.ready;

      // Fetch VAPID public key from backend
      const { data } = await api.get("/push/vapid-public-key");
      const applicationServerKey = urlBase64ToUint8Array(data.public_key);

      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey,
        });
      }

      const json = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: json.keys,
        user_agent: navigator.userAgent,
      });
      setSubscribed(true);
      toast.success(tx("ui_pushtoggle.k2c7ae9"));
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || tx("ui_pushtoggle.k08bbad");
      toast.error(`Impossible d'activer : ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        try {
          await api.post("/push/unsubscribe", { endpoint });
        } catch {}
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success(tx("ui_pushtoggle.keedbec"));
    } catch (e) {
      toast.error(tx("ui_pushtoggle.ke711ff"));
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/push/test");
      if (data?.sent > 0) toast.success(tx("tpl.test_sent", { n: data.sent }));
      else toast.error(tx("ui_pushtoggle.k9adef7"));
    } catch (e) {
      toast.error(tx("ui_pushtoggle.kdc436a"));
    } finally {
      setTesting(false);
    }
  };

  if (!supported) {
    return (
      <div data-testid="push-unsupported" className="card-c">
        <div className="flex items-start gap-3">
          <BellOff className="w-5 h-5 text-text-secondary shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="font-semibold">{tx("ui_pushtoggle.ka274f4")}</p>
            <p className="text-xs text-text-secondary mt-0.5">
              {tx("ui_pushtoggle.k10707e")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (iosNeedsInstall) {
    return (
      <div data-testid="push-ios-install" className="card-c">
        <div className="flex items-start gap-3">
          <Bell className="w-5 h-5 text-primary-c shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="font-semibold">{tx("ui_pushtoggle.k9b0ea6")}</p>
            <p className="text-xs text-text-secondary mt-0.5">
              {tx("ui_pushtoggle.k5b2fb5")}
              votre écran d'accueil. Appuyez sur <strong>{tx("ui_pushtoggle.k19e659")}</strong>,
              puis rouvrez Oresto depuis l'icône installée.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (permission === "denied") {
    return (
      <div data-testid="push-denied" className="card-c">
        <div className="flex items-start gap-3">
          <BellOff className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="font-semibold">{tx("ui_pushtoggle.kc8f68f")}</p>
            <p className="text-xs text-text-secondary mt-0.5">
              {tx("ui_pushtoggle.k2dbe08")}
              {tx("ui_pushtoggle.k8b6fe0")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="push-toggle-card" className="card-c">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <Bell className={`w-5 h-5 shrink-0 mt-0.5 ${subscribed ? "text-primary-c" : "text-text-secondary"}`} />
          <div className="min-w-0">
            <p className="font-semibold">{tx("ui_pushtoggle.kb9432d")}</p>
            <p className="text-xs text-text-secondary mt-0.5">
              {tx("ui_pushtoggle.kbbcc6d")}
              {tx("ui_pushtoggle.k47880a")}
            </p>
          </div>
        </div>
        {subscribed ? (
          <button
            data-testid="push-disable-btn"
            onClick={disable}
            disabled={busy}
            className="shrink-0 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : tx("ui_pushtoggle.k0ec85b")}
          </button>
        ) : (
          <button
            data-testid="push-enable-btn"
            onClick={enable}
            disabled={busy}
            className="shrink-0 rounded-lg bg-primary-c px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-c/90 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Activer"}
          </button>
        )}
      </div>
      {subscribed && (
        <div className="mt-3 flex items-center justify-between border-t border-black/5 pt-3">
          <p className="text-xs text-text-secondary">{tx("ui_pushtoggle.k4dcc4d")}</p>
          <button
            data-testid="push-test-btn"
            onClick={sendTest}
            disabled={testing}
            className="inline-flex items-center gap-1.5 rounded-md bg-surface-muted px-2.5 py-1 text-xs font-semibold text-primary-c hover:bg-[#ece8da] disabled:opacity-50"
          >
            {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {tx("ui_pushtoggle.k37b273")}
          </button>
        </div>
      )}
    </div>
  );
}
